import { JSX } from "react"
import { InnerHTMLProps, PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"

interface Highlight {
  key: string
  value: string
}

interface Field<T> {
  key: keyof T
  plain: string | null
  useHighlight?: boolean
}

interface HighlightedTextProps<T> extends Omit<InnerHTMLProps, "value"> {
  fields: Field<T>[]
  highlights?: ReadonlyArray<Highlight> | null
  joinWith?: string
}

export const HighlightedText = <T,>({
  fields,
  highlights,
  joinWith = " ",
  title,
  ...props
}: HighlightedTextProps<T>): JSX.Element => {
  const getHighlightedValue = (key: string, plain: string | null): string => {
    const fallback = plain ?? ""
    if (!highlights) return fallback
    const found = highlights.find((item) => item.key === key)
    return found ? found.value : fallback
  }

  const content = fields
    .map((f) => (f.useHighlight === false ? (f.plain ?? "") : getHighlightedValue(String(f.key), f.plain)))
    .filter((str) => str && str.trim() !== "")
    .join(joinWith)

  const defaultTitle = fields.reduce((acc, f) => {
    const trimmed = f.plain?.trim()
    if (trimmed) return acc ? acc + joinWith + trimmed : trimmed
    return acc
  }, "")

  return <PotentiallyUnsafeInnerHtml {...props} value={content} title={title ?? defaultTitle} />
}
